import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FaLayerGroup } from 'react-icons/fa';

// Importación de Contenedores L3
import L3ContainerTechnology from './L3ContainerTechnology';
import L3ContainerAlternative from './L3ContainerAlternative';
import L3ContainerExperience from './L3ContainerExperience';
import L3ContainerInnovation from './L3ContainerInnovation';
import L3ContainerAssistance from './L3ContainerAssistance';
import L3ContainerFinancial from './L3ContainerFinancial';
import L3ContainerVision from './L3ContainerVision';
import L3ContainerRegulation from './L3ContainerRegulation';

// Importación de Acciones
import { startLoadingTechnologiesByTextTechnologyPaginated } from '../../app/actions/technologyActions';
import { startLoadingAlternativesByTextAlternativePaginated } from '../../app/actions/alternativeActions';
import { startLoadingExperiencesByTextExperiencePaginated } from '../../app/actions/experienceActions';
import { startLoadingInnovationsByTextInnovationPaginated } from '../../app/actions/innovationActions';
import { startLoadingAssistancesByTextAssistancePaginated } from '../../app/actions/assistanceActions';
import { startLoadingFinancialsByTextFinancialPaginated } from '../../app/actions/financialActions';
import { startLoadingVisionsByTextVisionPaginatedNoCiiu } from '../../app/actions/visionActions';
import { startLoadingRegulationsByTextRegulationPaginated } from '../../app/actions/regulationActions';
import { startRegisterSearchInfo } from '../../app/actions/searchInfoActions';
import { debugLogPages } from '../../app/helpers/debugHelper';
import { setPreviousSearchParams } from '../../app/actions/searchHistoryActions';
import SemanticSearchModeBadge from './SemanticSearchModeBadge';

const L2RightContainer = ({
    searchTextState,
    catalogsToFilter,
    moduleSelected,
    catalogCiiuLevel1Selected,
    indicatorsToFilter,
    itemsPerPage,
    setItemsPerPage,
    groupVisionSelected,
    concernSelected
}) => {
    debugLogPages(':: L2RightContainer');

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [, setCurrentPageClick] = useState(0);

    // Selectores del Store
    const { user } = useSelector((state) => state.auth);
    const tech = useSelector((state) => state.technology);
    const alt = useSelector((state) => state.alternative);
    const exp = useSelector((state) => state.experience);
    const inn = useSelector((state) => state.innovation);
    const ass = useSelector((state) => state.assistance);
    const fin = useSelector((state) => state.financial);
    const vis = useSelector((state) => state.vision);
    const reg = useSelector((state) => state.regulation);

    //Coment 5jun2026
    //const prevParams = useSelector((state) => state.searchHistory);

    // 1. Mapeo de Acciones y Datos (Configuración dinámica)
    const moduleConfig = useMemo(() => ({
        technology: {
            Component: L3ContainerTechnology,
            action: startLoadingTechnologiesByTextTechnologyPaginated,
            data: tech.technologiesS, total: tech.totalItemsTechnologyS, pages: tech.totalPagesTechnologyS, current: tech.currentPageTechnologyS
        },
        alternative: {
            Component: L3ContainerAlternative,
            action: startLoadingAlternativesByTextAlternativePaginated,
            data: alt.alternativesS, total: alt.totalItemsAlternativeS, pages: alt.totalPagesAlternativeS, current: alt.currentPageAlternativeS
        },
        experience: {
            Component: L3ContainerExperience,
            action: startLoadingExperiencesByTextExperiencePaginated,
            data: exp.experiencesS, total: exp.totalItemsExperienceS, pages: exp.totalPagesExperienceS, current: exp.currentPageExperienceS
        },
        innovation: {
            Component: L3ContainerInnovation,
            action: startLoadingInnovationsByTextInnovationPaginated,
            data: inn.innovationsS, total: inn.totalItemsInnovationS, pages: inn.totalPagesInnovationS, current: inn.currentPageInnovationS
        },
        assistance: {
            Component: L3ContainerAssistance,
            action: startLoadingAssistancesByTextAssistancePaginated,
            data: ass.assistancesS, total: ass.totalItemsAssistanceS, pages: ass.totalPagesAssistanceS, current: ass.currentPageAssistanceS
        },
        financial: {
            Component: L3ContainerFinancial,
            action: startLoadingFinancialsByTextFinancialPaginated,
            data: fin.financialsS, total: fin.totalItemsFinancialS, pages: fin.totalPagesFinancialS, current: fin.currentPageFinancialS
        },
        vision: {
            Component: L3ContainerVision,
            //action: startLoadingVisionsByTextVisionPaginated,
            action: startLoadingVisionsByTextVisionPaginatedNoCiiu,
            data: vis.visionsS, total: vis.totalItemsVisionS, pages: vis.totalPagesVisionS, current: vis.currentPageVisionS
        },
        regulation: {
            Component: L3ContainerRegulation,
            action: startLoadingRegulationsByTextRegulationPaginated,
            data: reg.regulationsS, total: reg.totalItemsRegulationS, pages: reg.totalPagesRegulationS, current: reg.currentPageRegulationS
        }
    }), [tech, alt, exp, inn, ass, fin, vis, reg]);

    // 2. Función Unificada de Ejecución de Búsqueda
    const executeSearch = (page, size) => {
        //console.log("0000, En el hijo, executeSearch, page, size", page, size);
        //console.log("0000, En el hijo, prevParams, ", prevParams);

        const active = moduleConfig[moduleSelected?.key];
        if (!active || !searchTextState) return;

        const searchInfo = {
            text_search: searchTextState,
            indicators: indicatorsToFilter,
            filters: catalogsToFilter,
            catalogmodule_id: moduleSelected.id,
            //catalogciiu_id: catalogCiiuLevel1Selected?.id,
            user_id: user.user_id,
            deleted: false
        };

        dispatch(startRegisterSearchInfo(searchInfo));

        dispatch(setPreviousSearchParams({
            searchText: searchTextState,
            page: page,
            itemsPerPage: size,
            catalogsToFilter: catalogsToFilter,
            indicatorsToFilter: indicatorsToFilter,
            catalogModule: moduleSelected || {},
            catalogCiiuLevel1: catalogCiiuLevel1Selected || {},
            groupVision: groupVisionSelected || {},
            concern: concernSelected || {}
        }));

        if (catalogCiiuLevel1Selected) {
            dispatch(active.action(searchTextState, page, size, catalogsToFilter, indicatorsToFilter, catalogCiiuLevel1Selected.code));
        } else {
            dispatch(active.action(searchTextState, page, size, catalogsToFilter, indicatorsToFilter));
        }

    };

    // 3. Handlers de Eventos simplificados
    const onChangeSelect = (event) => {
        event.preventDefault();
        const newSize = event.target.value;
        //console.log(":: Cambiando tamaño a:", newSize); // RASTREO
        setItemsPerPage(newSize);
        setCurrentPageClick(0);
        executeSearch(0, newSize);
    };

    //2
    /*
    const onChangeSelect = (event) => {
        event.preventDefault();
        const newSize = parseInt(event.target.value, 10); // Aseguramos que sea número

        setItemsPerPage(newSize);
        setCurrentPageClick(0);

        // 1. Ejecutamos la búsqueda con el valor nuevo
        executeSearch(0, newSize);

        // 2. ¡IMPORTANTE! Actualizamos el historial inmediatamente con el valor nuevo
        // Esto evitará que el useEffect del padre use el valor por defecto (9)
        dispatch(setPreviousSearchParams({
            searchText: searchTextState,
            page: 0,
            //size: newSize, // <--- Forzamos el nuevo tamaño aquí
            itemsPerPage: newSize,
            catalogsToFilter: catalogsToFilter,
            indicatorsToFilter: indicatorsToFilter,
            catalogModule: moduleSelected || {},
            catalogCiiuLevel1: catalogCiiuLevel1Selected || {},
            groupVision: groupVisionSelected || {},
            concern: concernSelected || {}
        }));
    };*/

    //3
    /*
    const onChangeSelect = (event) => {
    event.preventDefault();
    const newSize = parseInt(event.target.value, 10);
    
    // 1. Actualizamos el estado del Padre PRIMERO
    setItemsPerPage(newSize);
    setCurrentPageClick(0);

    // 2. ACTUALIZACIÓN CRÍTICA: Sincronizamos Redux inmediatamente
    // Esto asegura que cuando el useEffect del Padre se dispare por el render,
    // lea el valor 'newSize' (6) desde prevParams y no el viejo (9).
    dispatch(setPreviousSearchParams({
        searchText: searchTextState,
        page: 0,
        //size: newSize, // <--- Forzamos el 6 aquí
        itemsPerPage: newSize,
        catalogsToFilter: catalogsToFilter,
        indicatorsToFilter: indicatorsToFilter,
        catalogModule: moduleSelected || {},
        catalogCiiuLevel1: catalogCiiuLevel1Selected || {},
        groupVision: groupVisionSelected || {},
        concern: concernSelected || {}
    }));

    // 3. Ejecutamos la búsqueda manual
    executeSearch(0, newSize);
};
*/
    //este funcaba verison 4
    /*
    const onChangeSelect = (event) => {
    event.preventDefault();
    const newSize = parseInt(event.target.value, 10);
    
    // 1. Actualizamos Redux PRIMERO para que el Padre vea el '6'
    dispatch(setPreviousSearchParams({
        ...prevParams, // mantén lo demás
        //size: newSize,
        itemsPerPage: newSize,
        page: 0
    }));

    // 2. Luego actualizamos el estado local/padre
    setItemsPerPage(newSize);
    setCurrentPageClick(0);

    // 3. Ejecutamos la búsqueda manual
    executeSearch(0, newSize);
};
*/

    const onPageChange = (pageClick) => {
        //console.log(":: L3Container triggering onPageChange to:", pageClick);
        //console.log(":: Click en Paginador (UI):", pageClick);
        //console.log(":: Solicitando a la API (Index 0):", pageClick - 1);
        setCurrentPageClick(pageClick);
        executeSearch(pageClick - 1, itemsPerPage);
    };

    // 4. Renderizado Dinámico
    const activeModule = moduleConfig[moduleSelected?.key];
    if (!activeModule) return null;

    const { Component, data, total, pages, current } = activeModule;

    return (
        <div className="animate__animated animate__fadeIn">
            <div className="results-status-container">
                <div className="results-badge">
                    <FaLayerGroup className="results-icon" />
                    <span className="results-text">
                        {t('general.results-obtained')}
                    </span>
                    <span className="results-divider">|</span>
                    <span className="results-number">{total}</span>
                </div>
                <SemanticSearchModeBadge moduleKey={moduleSelected?.key} />
            </div>

            <Component
                data={data}
                totalPagesS={pages}
                totalItemsS={total}
                currentPageS={current}
                onChangeSelect={onChangeSelect}
                onPageChange={onPageChange}
                itemsPerPage={itemsPerPage}
                moduleSelected={moduleSelected}
                catalogCiiuLevel1Selected={catalogCiiuLevel1Selected}
                groupVisionSelected={groupVisionSelected}
                concernSelected={concernSelected}
            />
        </div>
    );
};

export default L2RightContainer;
